import { useState, useCallback, useContext } from "react";
import { Dropdown } from "~/components/UI";
import OutboundVirtualTerminalsContext from "./OutboundVirtualsTerminalsContext";
import { useTranslation } from "react-i18next";
import { SOURCE_TYPE } from "~/api/common";
import useCreateIncidentDialog from "~/components/Incidents/CreateIncident/useCreateIncidentDialog";
import { PERMISSION } from "~/api/permissions/enums";
import usePermissions from "~/hook/usePermissions";

interface OutboundVirtualTerminalOptionsProps {
  outboundVirtualTerminal: OutboundVirtualTerminalsApi.OutboundVirtualTerminalDto;
}

/**
 * @memberof OutboundVirtualTerminals
 * @component
 * @desc OutboundVirtualTerminal options. Using a OutboundVirtualTerminalsContext.
 * @property {OutboundVirtualTerminalsApi.OutboundVirtualTerminalDto} outboundVirtualTerminal - OutboundVirtualTerminal data;
 * @requires OutboundVirtualTerminalsContext
 */

const OutboundVirtualTerminalOptions = ({
  outboundVirtualTerminal,
}: OutboundVirtualTerminalOptionsProps) => {
  const [loading, setLoading] = useState(false);
  const { onEdit, onUpdate, onUpdateItem, onDelete } = useContext(
    OutboundVirtualTerminalsContext
  );
  const [createIncident] = useCreateIncidentDialog(
    SOURCE_TYPE.OUTBOUND_VIRTUAL_TERMINAL
  );
  const { enabled } = outboundVirtualTerminal;
  const { hasAll, hasAny } = usePermissions();
  const { t } = useTranslation();

  const onUpdateHandler = useCallback((res: Promise<unknown> | void) => {
    if (res) {
      setLoading(true);
      res.finally(() => {
        setLoading(false);
      });
    }
  }, []);

  const onCreatedIncident = useCallback(
    (incident: IncidentsApi.IncidentDto) => {
      if (outboundVirtualTerminal.incidents) {
        outboundVirtualTerminal.incidents.count++;
        outboundVirtualTerminal.incidents.incidentIds.push(incident.incidentId);
      } else {
        outboundVirtualTerminal.incidents = {
          count: 1,
          incidentIds: [incident.incidentId],
        };
      }
      onUpdateItem && onUpdateItem(outboundVirtualTerminal);
    },
    [outboundVirtualTerminal, onUpdateItem]
  );

  if (
    !hasAny([
      PERMISSION.EDIT_OUTBOUND_VIRTUAL_TERMINAL,
      PERMISSION.DELETE_OUTBOUND_VIRTUAL_TERMINAL,
      PERMISSION.CREATE_JOURNAL_INCIDENTS,
    ])
  ) {
    return null;
  }

  const options = [
    {
      label: enabled ? t("option.disable") : t("option.enable"),
      hidden: !hasAll(PERMISSION.EDIT_OUTBOUND_VIRTUAL_TERMINAL),
      confirmation: {
        cancelText: t("button.no"),
        okText: t("button.yes"),
        title: t("text.confirmationRequest"),
        onConfirm: () =>
          onUpdate &&
          onUpdateHandler(
            onUpdate(outboundVirtualTerminal.id, { enabled: !enabled })
          ),
      },
    },
    {
      label: t("option.edit"),
      hidden: !hasAll(PERMISSION.EDIT_OUTBOUND_VIRTUAL_TERMINAL),
      onClick: () => onEdit && onEdit(outboundVirtualTerminal),
    },
    {
      label: t("option.delete"),
      hidden: !hasAll(PERMISSION.DELETE_OUTBOUND_VIRTUAL_TERMINAL),
      onClick: () =>
        onDelete && onUpdateHandler(onDelete(outboundVirtualTerminal)),
    },
    {
      label: t("option.createIncident"),
      hidden: !hasAll(PERMISSION.CREATE_JOURNAL_INCIDENTS),
      onClick: () =>
        createIncident(outboundVirtualTerminal.id, onCreatedIncident),
    },
  ];

  return <Dropdown loading={loading} options={options} />;
};

export default OutboundVirtualTerminalOptions;
